import React from 'react';
import {
  ArrowForward as ArrowForwardIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { BlogPost } from '../../types';

interface BlogCardProps {
  post: BlogPost;
  variant?: 'default' | 'featured';
  onClick?: () => void;
  className?: string;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const BlogCard: React.FC<BlogCardProps> = ({
  post,
  variant = 'default',
  onClick,
  className = '',
}) => {
  const interactiveProps = onClick
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};

  if (variant === 'featured') {
    return (
      <article
        className={`blog-card blog-card--featured group overflow-hidden rounded-2xl border border-white/10 bg-brand-graphite ${
          onClick ? 'cursor-pointer' : ''
        } ${className}`}
        {...interactiveProps}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="flex flex-col justify-center px-6 py-8 sm:px-8 sm:py-10 lg:py-12">
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span className="blog-card__badge-featured">À la une</span>
              <span className="blog-card__badge-category">{post.category}</span>
            </div>

            <h2 className="text-2xl font-bold leading-tight text-white transition-colors group-hover:text-orange-400 sm:text-3xl lg:text-[2rem]">
              {post.title}
            </h2>

            <p className="mt-4 line-clamp-4 text-base leading-relaxed text-white/60 sm:text-[1.0625rem]">
              {post.excerpt}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/55">
              <span className="inline-flex items-center gap-1.5">
                <CalendarIcon className="h-4 w-4 text-orange-500" aria-hidden />
                {formatDate(post.publishedAt)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ScheduleIcon className="h-4 w-4 text-orange-500" aria-hidden />
                {post.readTime} min de lecture
              </span>
            </div>

            <span className="mt-7 inline-flex items-center gap-1.5 text-sm font-bold text-orange-500 transition-colors group-hover:text-orange-400">
              Lire l&apos;article
              <ArrowForwardIcon
                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
                aria-hidden
              />
            </span>
          </div>

          <div className="relative min-h-[240px] overflow-hidden bg-neutral-900 lg:min-h-[320px]">
            <img
              src={post.image}
              alt=""
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              loading="eager"
            />
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      className={`blog-card group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-brand-graphite transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-white/16 hover:shadow-[0_12px_32px_-10px_rgba(0,0,0,0.45)] ${
        onClick ? 'cursor-pointer' : ''
      } ${className}`}
      {...interactiveProps}
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-neutral-900">
        <img
          src={post.image}
          alt=""
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <h3 className="line-clamp-2 text-lg font-bold leading-snug text-white transition-colors group-hover:text-orange-400 sm:text-xl">
          {post.title}
        </h3>

        <div className="mt-auto flex items-end justify-between gap-4 pt-5">
          <div className="flex flex-col gap-1.5 text-[12px] text-white/55 sm:text-[13px]">
            <span className="inline-flex items-center gap-1.5">
              <CalendarIcon className="h-3.5 w-3.5 text-orange-500" aria-hidden />
              {formatDate(post.publishedAt)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ScheduleIcon className="h-3.5 w-3.5 text-orange-500" aria-hidden />
              {post.readTime} min de lecture
            </span>
          </div>

          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-orange-500/35 bg-orange-500/10 text-orange-500 transition-colors group-hover:border-orange-500 group-hover:bg-orange-500/20"
            aria-hidden
          >
            <ArrowForwardIcon className="h-[1.1rem] w-[1.1rem]" />
          </span>
        </div>
      </div>
    </article>
  );
};

export default BlogCard;
